import { color, useLink, formatDate, useAuthedRequestFn, useNavigate, RandomId, FormatUtils, safeBigNum } from '@my/util'
import { YStack, XStack, Text, FinancialTable, View, ActionButton, ActiveButton, SimpleText, Spinner, Button, useToastController, Image, ScrollView } from '@my/ui'
import { DEFAULT_TABLE_DATA, SCENARIO_CHART_DATA } from '../helpers'
import { FinancialReviewHeading } from '../../../components/index'
import { ScenarioChart } from './scenarioChart'
import images from '../../../assets/images'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ProfileChangesListItem, type ScenarioProfileData } from './scenarioFinancialProfile'
import { useMemo, useState } from 'react'
import { ChevronDown, Info, Minus, Plus, Square, CircleX } from '@tamagui/lucide-icons-2'
import { Platform } from 'react-native'

export const TABLE_HEADINGS = [
  //{ key: 'label', text: 'Completed' },
  { key: 'expander', text: '' },
  { key: 'name', text: 'Goal Name' },
  { key: 'amount', text: 'Target Amount' },
  //{ key: 'RemainingAmount', text: 'Remaining Amount' },
  { key: 'timeline', text: 'Timeline' },
  { key: 'potential', text: 'Success Probability', info: true, infoText: "Your likelihood of hitting this goal across thousands of 'what if' scenarios factoring in market projections, savings rate, and timeline. Lower score? No stress - small tweaks can make a big difference." },
  { key: 'priority', text: 'Priority' },
  { key: 'actionButtons', text: 'Actions' },
]

const TableSection = ({ title, data }: { title: string; data: ScenarioProfileData[] }) => (
  <View gap={20}>
    {data.length ? <XStack justifyContent="flex-start" alignItems="center" width="100%">
      <Text
        color="#555555"
        style={{ fontSize: 16, lineHeight: 19, fontWeight: '700', marginLeft: 10 }}
      >
        {title}
      </Text>
    </XStack> : null}
    <FinancialTable 
      data={data} headings={TABLE_HEADINGS} 
      expandProps={{
        getRowCanExpand: (row) => { return true},
        renderSubComponent: (row) => {
          const profileChangesList = row.original['profileChangesList'];
          return <YStack gap={10} padding={15} width="100%" bc="rgba(133, 193, 157, .1)">
            <YStack>
              <SimpleText fontWeight={700} color="rgb(108, 117, 125)">Description:</SimpleText>
              <Text>{row.original['description'] || '--'}</Text>
            </YStack>
            <YStack maxWidth={'50%'}>
              {profileChangesList.length && <SimpleText fontWeight={700} color="rgb(108, 117, 125)">Profile Changes:</SimpleText>}
              {row.original['profileChangesList'].map((item, idx) => <ProfileChangesListItem key={idx} {...item} />)}
            </YStack>
          </YStack>
        }
      }} 
      mobileRenderOptions={{
        titleKey: 'name',
        mainValueKeys: ['amount', 'timeline'],
        interactiveKeys: ['actionButtons'],
        otherKeys: [['priority', 'potential'], ['expander']]
      }}
      noDataMessage='No scenarios available. Go ahead and create your first scenario!'
    />
  </View>
)

export const ScenarioPlanning = () => {
  const toast = useToastController()
  const queryClient = useQueryClient()
  const [notice, setNotice] = useState<boolean>(true)
  const authedRequestFn = useAuthedRequestFn();
  const navigate = useNavigate();
  // Fetch data
  const { data, isLoading } = useQuery({
    queryKey: ['financial-scenarios'],
    queryFn: () => authedRequestFn(`/financial-scenarios`),
  })

  const userScenarios = data?.scenarios || []

  const deleteFinancialGoal = useMutation({
    mutationFn: (payload: any) =>
      authedRequestFn(`/financial-scenarios/${payload.scenarioId}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['financial-scenarios'] })
      toast.show('Scenario Deleted!')
    },
    onError: () => {
      toast.show('Error deleting Scenario.')
    },
  })

  // Goals mapping logic
    const scenariosTableData = useMemo(() => {
      return userScenarios?.map((scenario) => {
        const succPotentialBn = safeBigNum(scenario.scenarioGoals[0].successPotential);
        return {
          expander: {
            expander: {
              renderFn: ({ onClick, isExpanded }) => {
                return <XStack alignItems='center'><Text color={color.lightGrey} fontWeight={700}>Details:</Text><Button
                  {...{
                    onClick,
                    style: { cursor: 'pointer', marginLeft: 10 },
                  }}
                  //icon={isExpanded ? Minus : Plus}
                >
                                  <ChevronDown size="$1" transition="quick" rotate={isExpanded ? '180deg' : '0deg'}/>
                                </Button></XStack>
              }
            }
          },
          name: scenario.scenarioGoals[0].goalName,
          amount: `${FormatUtils.currency.cashFormat(safeBigNum(scenario.scenarioGoals[0].targetAmount))}`,
          timeline: FormatUtils.dateTime['Month yyyy'](scenario.scenarioGoals[0].targetDate),
          potential: succPotentialBn.isNaN() ? '- -' : succPotentialBn.toFixed(2) + '%',
          priority: { 
            priorityBadge: true,
            priority: scenario.scenarioGoals[0].priority,
          },
          actionButtons: {
            edit: () => navigate('/create-scenario?scenarioId='+scenario.scenarioId),
            delete: () => deleteFinancialGoal.mutate({scenarioId: scenario.scenarioId}),
          },
          description: scenario.scenarioGoals[0].description,
          profileChangesList: Object.entries(scenario.profileDelta || {})
            .map(([key, value]) => ({ label: key, ...(value || {}) })),
        }
    })
    }, [userScenarios])

  const createScenarioLinkProps = useLink('/create-scenario');
  const output = <YStack p="$4" gap={10}>
    {/* Header */}
    <YStack
      gap={10}
      flexWrap="wrap"
      flexDirection="row"
      justifyContent="space-between"
      alignItems={'center'}
    >
      <YStack>
        <FinancialReviewHeading
          title="Scenario Planning"
          //type="Planning"
          img={images.scenarioPlanning}
          imageStyle={{
            //'$platform-native': { width: 15, height: 19 },
            //'$platform-web': { width: 20, height: 26 },
          }}
        />
      </YStack>
      {scenariosTableData.length > 0 ? null : <ActiveButton
        {...createScenarioLinkProps}
        style={{width: 160, padding: 0}}
        ButtonTextProps={{children: 'Create Scenario'}}
      />}
    </YStack>
    {/* Notice Section */}
    {notice && <NoticeSection setNotice={setNotice} />}
    {/* Income, Expenses, Savings, Investments, and Financial Goals Tables */}
    {isLoading ? (
      <Spinner size="large" color="$green10" />
    ) : (
      <TableSection title="Your Scenarios" data={scenariosTableData} />
    )}
    
  </YStack>;


  
  return (
    Platform.OS === 'web' ? (
      output
    ) : (
      <ScrollView backgroundColor={color.white}>
        {output}
      </ScrollView>
    )
  );
}

const NoticeSection = ({ setNotice }) => (
  <XStack style={{position: 'relative'}} gap={10} backgroundColor={'rgb(243, 245, 247)'} padding={'$4'} borderRadius={10} borderWidth={1} borderColor={'rgba(220, 224, 229, 0.6)'}>
    <ActionButton
      unstyled
      circular
      onPress={() => setNotice(false)}
      style={{ position: 'absolute', top: 2, right: 2 }}
      hoverStyle={{backgroundColor: 'rgba(188, 188, 188, 0.66)'}}
      pressStyle={{backgroundColor: 'rgba(188, 188, 188, 0.66)'}}
    >
      <CircleX />
    </ActionButton>
    <XStack mb={'$2'}>
      <Info width={20} height={20} />
    </XStack>
    <SimpleText fontSize={14} color={'rgb(102, 102, 102)'}>
      One scenario is free. Additional scenarios require a{' '}
      <SimpleText
        fontWeight={'500'}
        fontSize={14}
        color={color.tzaGreen}
        //textDecorationLine="underline"
        hoverStyle={{cursor: 'pointer'}}
        //onPress={() => alert('Premium subscriptions coming soon!')}
      >
        premium subscription.
      </SimpleText>
    </SimpleText>
  </XStack>
)